/**
 * Пауза при сворачивании и «выход» из забежки после 10 минут в фоне (решения 2026-10-24: бой идёт только пока игра
 * открыта; усиления живут до проигрыша или выхода — закрыл / свёрнуто > 10 мин).
 *
 * Свёрнуто = `document.visibilityState === "hidden"` или событие Telegram `deactivated` (Bot API 8.0: Mini App свёрнут
 * в шторку, страница при этом может оставаться «видимой»). В фоне WebView часто замораживает таймеры — поэтому
 * время отсутствия проверяется и при возвращении, а таймер на 10 минут лишь ускоряет выход там, где JS продолжает жить
 * (десктоп). Выход — один раз за одно сворачивание.
 *
 * «Закрыл» клиент сам не сообщает (pagehide в Telegram ненадёжен, а при перезагрузке страницы сжёг бы усиления) —
 * брошенную забежку закрывает сервер.
 */

/** Сколько можно провести в фоне без потери забежки. */
export const BACKGROUND_EXIT_MS = 10 * 60 * 1000;

export interface Clock {
  now(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

export const systemClock: Clock = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (h) => globalThis.clearTimeout(h as ReturnType<typeof setTimeout>),
};

export interface BackgroundWatcherOptions {
  clock?: Clock;
  exitAfterMs?: number;
  onPause(): void;
  onResume(): void;
  /** Пробыл в фоне дольше `exitAfterMs` — забежку закрыть (`exitRun` с `backgroundTimeout`). */
  onExit(awayMs: number): void;
}

export interface BackgroundWatcher {
  /** Источник `source` сообщил, что приложение свёрнуто / снова видно. Свёрнуто, пока свёрнуто хоть по одному. */
  setHidden(source: string, hidden: boolean): void;
  readonly hidden: boolean;
  dispose(): void;
}

export function createBackgroundWatcher(opts: BackgroundWatcherOptions): BackgroundWatcher {
  const clock = opts.clock ?? systemClock;
  const exitAfter = opts.exitAfterMs ?? BACKGROUND_EXIT_MS;
  const sources = new Set<string>();
  let hiddenAt = 0;
  let exited = false;
  let timer: unknown = null;

  const stopTimer = () => {
    if (timer !== null) clock.clearTimeout(timer);
    timer = null;
  };
  const exit = () => {
    if (exited) return;
    exited = true;
    opts.onExit(clock.now() - hiddenAt);
  };

  return {
    get hidden() {
      return sources.size > 0;
    },
    setHidden(source, hidden) {
      const was = sources.size > 0;
      if (hidden) sources.add(source);
      else sources.delete(source);
      const now = sources.size > 0;
      if (was === now) return;
      if (now) {
        hiddenAt = clock.now();
        exited = false;
        opts.onPause();
        timer = clock.setTimeout(exit, exitAfter);
      } else {
        stopTimer();
        if (clock.now() - hiddenAt >= exitAfter) exit();
        opts.onResume();
      }
    },
    dispose: stopTimer,
  };
}
